import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  getTaskDefinitionByIdQueryControllerGet,
  getTaskInstanceByIdQueryControllerGet,
} from "@/generated/compliance-tasks/compliance-tasks";
import type {
  ComplianceTaskDefinitionResponseDto,
  ComplianceTaskInstanceResponseDto,
} from "@/generated/model";
import { offlineDb } from "@/lib/offline/db";

interface OfflineComplianceInstanceState {
  instance: ComplianceTaskInstanceResponseDto | null;
  definition: ComplianceTaskDefinitionResponseDto | null;
  isLoading: boolean;
  isOffline: boolean;
  /** Rendering the cached copy because no fetch has succeeded this mount. */
  isServedFromCache: boolean;
  error: Error | null;
}

/**
 * Read-side hook for the compliance task completion screen.
 *
 * Fetches the instance + its definition from the API when online and caches
 * both into IndexedDB; on offline reload, serves the cached pair so the
 * completion screen works fully offline. The instance is the entry point —
 * we always look up its definition by `instance.taskDefinitionId`, never
 * the other way round.
 */
export function useOfflineComplianceTaskInstance(instanceId: string) {
  const [cached, setCached] = useState<{
    instance: ComplianceTaskInstanceResponseDto | null;
    definition: ComplianceTaskDefinitionResponseDto | null;
  }>({ instance: null, definition: null });
  const [cacheLoaded, setCacheLoaded] = useState(false);

  // Hydrate from IndexedDB cache on mount (synchronously available offline).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const row = await offlineDb.complianceInstances.get(instanceId);
      if (cancelled) return;
      if (row) {
        setCached({
          instance: row.instance as ComplianceTaskInstanceResponseDto,
          definition: row.definition as ComplianceTaskDefinitionResponseDto,
        });
      }
      setCacheLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  // Fresh fetch via TanStack Query — disabled until cache hydration completes
  // so we don't double-render with mismatched state.
  const query = useQuery({
    queryKey: ["compliance-instance-with-definition", instanceId],
    enabled: cacheLoaded,
    queryFn: async () => {
      const instanceResponse =
        await getTaskInstanceByIdQueryControllerGet(instanceId);
      const instance = instanceResponse.data;
      const definitionResponse = await getTaskDefinitionByIdQueryControllerGet(
        instance.taskDefinitionId,
      );
      const definition = definitionResponse.data;

      // Update cache. Cache is keyed by instanceId so completing one instance
      // doesn't disturb others.
      await offlineDb.complianceInstances.put({
        id: instance.id,
        definitionId: definition.id,
        nurseryId: instance.nurseryId,
        instance,
        definition,
        cachedAt: Date.now(),
      });

      return { instance, definition };
    },
    // Standard offline-first config: cached data is immediately fresh enough
    // to render; refetch on focus/reconnect picks up any server-side changes.
    staleTime: 30_000,
    // Both overrides are off globally. Evidence download URLs are signed for
    // five minutes, so without them a record left open (or reopened from cache)
    // hands out links that resolve to a storage error.
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const isOffline = !navigator.onLine;
  const instance = query.data?.instance ?? cached.instance;
  const definition = query.data?.definition ?? cached.definition;

  const state: OfflineComplianceInstanceState = {
    instance,
    definition,
    isLoading: !cacheLoaded || (query.isLoading && !instance),
    isOffline,
    isServedFromCache: !query.data && !!cached.instance,
    error: query.error as Error | null,
  };

  return {
    ...state,
    refetch: query.refetch,
  };
}
